import type { CSSProperties } from 'react';
import { Check, Gift, Loader2 } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import {
  deductionNote,
  formatEuros,
  isOfferPurchasable,
  perCardCents,
  type SkillcardQuote,
  offerContentLabel,
} from '@jeraisonne/db/cards';

/**
 * Une offre d'achat (carte / pack / programme) : prix, prix moyen par carte,
 * économie par rapport à l'achat carte par carte. Les cartes déjà dans le deck
 * sont déduites par le serveur (`cards_to_buy`).
 */
export function SkillcardOfferRow({
  quote,
  title,
  subtitle,
  highlight,
  busy,
  disabled,
  accent,
  background,
  onBuy,
  onAskPay,
  askBusy,
  buyLabel = 'Acheter',
  hideDeductionNote = false,
}: {
  quote: SkillcardQuote;
  title: string;
  subtitle?: string;
  highlight?: string;
  busy?: boolean;
  disabled?: boolean;
  /** Teinte de la carte (hex, `cardPalette().accent`) : l'offre prend son thème. */
  accent?: string;
  /** Fond de la carte (hex, `cardPalette().bg`), avec `accent`. */
  background?: string;
  onBuy: () => void;
  /** « Faire payer par un proche » : partage un lien de paiement (compte connecté). */
  onAskPay?: () => void;
  askBusy?: boolean;
  /** Libellé du bouton (« Offrir », « Acheter pour Léa »…). */
  buyLabel?: string;
  /** Carte cadeau : prix plein, pas de mention de déduction. */
  hideDeductionNote?: boolean;
}) {
  const buyable = isOfferPurchasable(quote);
  const owned = quote.cards_to_buy === 0;
  const partial = !owned && quote.cards_to_buy < quote.total_cards;
  const themed = !!accent;
  // Teinte de la carte en variables CSS (seules valeurs non statiques).
  const vars = themed
    ? ({ '--jr-o-accent': accent, '--jr-o-bg': background ?? '#0a1638' } as CSSProperties)
    : undefined;
  const price = formatEuros(quote.price_cents);
  // Promo SKILLCARDS en cours (migration 0717) : prix d'avant barré.
  const promo = quote.promo && quote.promo.before_cents > quote.price_cents ? quote.promo : null;
  // Déduction plafonnée de ce qui est déjà acheté (migration 0320) : toujours dite.
  const note = owned || hideDeductionNote ? null : deductionNote(quote);
  return (
    <div
      style={vars}
      data-themed={themed || undefined}
      className={cn(
        'rounded-2xl border p-4',
        themed
          ? cn(
              'bg-[linear-gradient(135deg,color-mix(in_srgb,var(--jr-o-accent)_22%,var(--jr-o-bg))_0%,var(--jr-o-bg)_75%)] text-white',
              highlight
                ? 'border-[var(--jr-o-accent)] ring-2 ring-[color-mix(in_srgb,var(--jr-o-accent)_35%,transparent)]'
                : 'border-[color-mix(in_srgb,var(--jr-o-accent)_40%,transparent)]',
            )
          : cn(
              'bg-white',
              highlight ? 'border-primary-300 ring-primary-100 ring-2' : 'border-ink-200',
            ),
      )}
    >
      {highlight && (
        <p
          className={cn(
            'mb-1 text-xs font-bold uppercase tracking-wider',
            themed ? 'text-[var(--jr-o-accent)]' : 'text-primary-600',
          )}
        >
          {highlight}
        </p>
      )}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'font-display text-base font-bold',
              themed ? 'text-white' : 'text-ink-900',
            )}
          >
            <MathText>{title}</MathText>
          </p>
          <p className={cn('text-sm', themed ? 'text-white/70' : 'text-ink-500')}>
            {subtitle ?? offerContentLabel(quote)}
            {partial && ` · ${quote.cards_to_buy} à ajouter à ton deck`}
          </p>
          {buyable && quote.cards_to_buy > 1 && (
            <p className={cn('mt-0.5 text-xs', themed ? 'text-white/65' : 'text-ink-500')}>
              soit {formatEuros(perCardCents(quote))} la carte
              {quote.savings_pct > 0 && (
                <span
                  className={cn(
                    'ml-2 rounded-full px-2 py-0.5 font-semibold',
                    themed
                      ? 'bg-[color-mix(in_srgb,var(--jr-o-accent)_25%,transparent)] text-white'
                      : 'bg-success-50 text-success-700',
                  )}
                >
                  −{quote.savings_pct} %
                </span>
              )}
            </p>
          )}
          {note && (
            <p className={cn('mt-1 text-xs', themed ? 'text-white/65' : 'text-ink-500')}>{note}</p>
          )}
        </div>
        {!owned && buyable && (
          <div className="shrink-0 text-right">
            {promo && (
              <p
                className={cn(
                  'mb-1 text-sm font-semibold line-through',
                  themed ? 'text-white/60' : 'text-ink-400',
                )}
              >
                <span className="sr-only">Au lieu de </span>
                {formatEuros(promo.before_cents)}
              </p>
            )}
            <p
              className={cn(
                'font-display text-3xl font-extrabold leading-none',
                themed ? 'text-white' : 'text-ink-900',
              )}
            >
              {price}
            </p>
            {promo && (
              <p className="bg-accent-500 mt-1.5 inline-block rounded-full px-2 py-0.5 text-xs font-bold text-[#061433]">
                Promo −{promo.percent}&nbsp;%
              </p>
            )}
          </div>
        )}
      </div>
      {owned ? (
        <p
          className={cn(
            'mt-2 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold',
            themed ? 'text-[var(--jr-o-accent)]' : 'text-success-700',
          )}
        >
          <Check className="h-4 w-4" aria-hidden /> Déjà dans ton deck
        </p>
      ) : (
        <button
          type="button"
          onClick={onBuy}
          disabled={!buyable || busy || disabled}
          aria-label={`${buyLabel} · ${price}`}
          className={cn(
            'font-display mt-3 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold shadow active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none disabled:active:scale-100',
            themed
              ? 'bg-[var(--jr-o-accent)] text-[var(--jr-o-bg)] hover:brightness-110'
              : 'bg-primary-500 hover:bg-primary-600 text-white',
          )}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {busy ? 'Redirection…' : buyLabel}
        </button>
      )}
      {!owned && buyable && onAskPay && (
        <button
          type="button"
          onClick={onAskPay}
          disabled={askBusy || busy || disabled}
          className={cn(
            // Action secondaire répétée sous chaque offre : en lien discret, pas en 2e gros bouton.
            'mt-1 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold underline-offset-2 hover:underline active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100',
            themed ? 'text-white/85' : 'text-ink-700',
          )}
        >
          {askBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Gift className="h-4 w-4" aria-hidden />
          )}
          Faire payer par un proche
        </button>
      )}
    </div>
  );
}
